import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Button,
  SearchForm,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc/ui-design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { RetailerNetworkUserCreate, Retailer_Network_User_Role, User_Role } from '@occc/graphql';
import { useGetEligibleForRetailerConnectUsers } from '@occc-app/dataAccessLayer/openCommerceUser/queries';
import { pluralize } from '@occc-app/lib/strings';

import RetailerNetworkUsersList from './retailer-network-users-list.component';

type RetailerNetworkUsersListContainerProps = {
  retailerNetworkUpdate: (newRetailerNetworkUsers: RetailerNetworkUserCreate[]) => Promise<void>;
  onClose: () => void;
};

export const RetailerNetworkUsersListContainer = ({
  retailerNetworkUpdate,
  onClose,
}: RetailerNetworkUsersListContainerProps) => {
  const { networkId = '' } = useParams<{ networkId: string }>();
  const [userSearch, setUserSearch] = useState<string | undefined>('');

  const { loading, error, data, paginationProps } = useGetEligibleForRetailerConnectUsers({
    filter: {
      search: userSearch,
      notInRetailerNetworkUuid: [networkId],
      userRoleContains: [User_Role.RcUser],
    },
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const onSubmit = async () => {
    const selectedItems = Array.from(bulkSelectedItems);
    const newRetailerNetworkUsers = selectedItems.map((userUuid) => ({
      userUuid,
      role: Retailer_Network_User_Role.RetailerNetworkUser,
    }));
    await retailerNetworkUpdate(newRetailerNetworkUsers);
    onClose();
  };

  const headerActions: React.ReactNode = useMemo(() => {
    return (
      <SearchForm
        onSubmit={({ search }) => {
          setUserSearch(search);
        }}
      />
    );
  }, [setUserSearch]);

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  return (
    <TableContainer>
      <TableHeader
        disableTitleTypography
        actions={headerActions}
        title={<Typography variant='h6'>Choose Users to Add to Retailer Network</Typography>}
        bulkSelected={bulkSelectedItemsCount}
        bulkActions={
          <Button color='primary' variant='contained' onClick={onSubmit}>
            {`Add selected ${pluralize(bulkSelectedItemsCount, 'User')}`}
          </Button>
        }
      />
      <RetailerNetworkUsersList
        loading={loading}
        data={data}
        error={error}
        paginationProps={paginationProps}
        bulkSelectedItems={bulkSelectedItems}
        handleBulkSelectMultiple={handleBulkSelectMultiple}
        handleBulkDeselectAll={handleBulkDeselectAll}
        handleItemSelect={handleItemSelect}
      />
    </TableContainer>
  );
};
